/**
 * VPE Dashboard State Hook
 * Manages all state for the VPEDashboard component
 * Extracted from VPEDashboard.tsx for better separation of concerns
 */

import { useState } from 'react';
import { Group, Patient } from '@/services/ea/vpeDashboardService';

export interface VPEDashboardState {
  // View Management
  currentView: string;
  
  // Search and Filter States
  searchTerm: string;
  searchBy: string;
  filteredGroups: Group[];
  
  // Pagination States
  currentPage: number;
  rowsPerPage: number;
  
  // Loading States
  isRefreshing: boolean;
  isArchiveLoading: boolean;
  isDeleteLoading: boolean;
  archiveButtonLoading: number | null;
  
  // Dialog States
  showLogoutDialog: boolean;
  showEmailDialog: boolean;
  showArchiveDialog: boolean;
  showDeleteDialog: boolean;
  
  // Selected Items
  selectedGroupId: number | null;
  duplicateGroupId: number | null;
  editGroupId: number | null;
  
  // Patient Search States
  patientSearchBy: string;
  patientSearchTerm: string;
  searchedPatients: Patient[];
  selectedPatient: Patient | null;
  isPatientSearchLoading: boolean;
  patientCount: number;
  showSearchDropdown: boolean;
}

export interface VPEDashboardStateActions {
  // View Management
  setCurrentView: (view: string) => void;
  
  // Search and Filter Actions
  setSearchTerm: (term: string) => void;
  setSearchBy: (searchBy: string) => void;
  setFilteredGroups: (groups: Group[]) => void;
  
  // Pagination Actions
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  
  // Loading Actions
  setIsRefreshing: (loading: boolean) => void;
  setIsArchiveLoading: (loading: boolean) => void;
  setIsDeleteLoading: (loading: boolean) => void;
  setArchiveButtonLoading: (id: number | null) => void;
  
  // Dialog Actions
  setShowLogoutDialog: (show: boolean) => void;
  setShowEmailDialog: (show: boolean) => void;
  setShowArchiveDialog: (show: boolean) => void;
  setShowDeleteDialog: (show: boolean) => void;
  
  // Selected Items Actions
  setSelectedGroupId: (id: number | null) => void;
  setDuplicateGroupId: (id: number | null) => void;
  setEditGroupId: (id: number | null) => void;
  
  // Patient Search Actions
  setPatientSearchBy: (searchBy: string) => void;
  setPatientSearchTerm: (term: string) => void;
  setSearchedPatients: (patients: Patient[]) => void;
  setSelectedPatient: (patient: Patient | null) => void;
  setIsPatientSearchLoading: (loading: boolean) => void;
  setPatientCount: (count: number) => void;
  setShowSearchDropdown: (show: boolean) => void;
  
  // Utility Actions
  resetSearch: () => void;
  resetPatientSearch: () => void;
}

export const useVPEDashboardState = () => {
  // View Management
  const [currentView, setCurrentView] = useState('dashboard');
  
  // Search and Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('group_name');
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Loading States
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [archiveButtonLoading, setArchiveButtonLoading] = useState<number | null>(null);
  
  // Dialog States
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Selected Items
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [duplicateGroupId, setDuplicateGroupId] = useState<number | null>(null);
  const [editGroupId, setEditGroupId] = useState<number | null>(null);
  
  // Patient Search States
  const [patientSearchBy, setPatientSearchBy] = useState('name');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [searchedPatients, setSearchedPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isPatientSearchLoading, setIsPatientSearchLoading] = useState(false);
  const [patientCount, setPatientCount] = useState(306);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  // Utility Functions
  const resetSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const resetPatientSearch = () => {
    setPatientSearchTerm('');
    setSearchedPatients([]);
    setSelectedPatient(null);
    setShowSearchDropdown(false);
  };

  const state: VPEDashboardState = {
    currentView,
    searchTerm,
    searchBy,
    filteredGroups,
    currentPage,
    rowsPerPage,
    isRefreshing,
    isArchiveLoading,
    isDeleteLoading,
    archiveButtonLoading,
    showLogoutDialog,
    showEmailDialog,
    showArchiveDialog,
    showDeleteDialog,
    selectedGroupId,
    duplicateGroupId,
    editGroupId,
    patientSearchBy,
    patientSearchTerm,
    searchedPatients,
    selectedPatient,
    isPatientSearchLoading,
    patientCount,
    showSearchDropdown,
  };

  const actions: VPEDashboardStateActions = {
    setCurrentView,
    setSearchTerm,
    setSearchBy,
    setFilteredGroups,
    setCurrentPage,
    setRowsPerPage,
    setIsRefreshing,
    setIsArchiveLoading,
    setIsDeleteLoading,
    setArchiveButtonLoading,
    setShowLogoutDialog,
    setShowEmailDialog,
    setShowArchiveDialog,
    setShowDeleteDialog,
    setSelectedGroupId,
    setDuplicateGroupId,
    setEditGroupId,
    setPatientSearchBy,
    setPatientSearchTerm,
    setSearchedPatients,
    setSelectedPatient,
    setIsPatientSearchLoading,
    setPatientCount,
    setShowSearchDropdown,
    resetSearch,
    resetPatientSearch,
  };

  return {
    state,
    actions,
  };
};